import React, { useState, useEffect, useContext, useRef } from 'react';
import './SwitchTenantModal.css';
import apiClient from '../../utils/apiClient';
import ConfirmationModal from './ConfirmationModal';
import { ProfileContext } from '../../contexts/ProfileContext';
import TokenService from '../../utils/tokenService';
import { useDispatch } from 'react-redux';
import { resetTheme } from '../../store/themeSlice';
import BrandingService from '../../utils/brandingService';

const SwitchTenantModal = ({ isOpen, onClose }) => {
  const { refreshProfile } = useContext(ProfileContext);
  const dispatch = useDispatch();

  const [searchQuery, setSearchQuery] = useState('');
  const [tenants, setTenants] = useState([]);
  const [filteredTenants, setFilteredTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const modalRef = useRef(null);

  const currentTenant = TokenService.getIdTokenInfo()?.['custom:tenant'] || '';

  useEffect(() => {
    const fetchTenants = async () => {
      try {
        const res = await apiClient.get('/tenants');
        const allTenants = res.data.tenants || [];
        const available = allTenants.filter(
          t => t.TenantName !== currentTenant
        );
        setTenants(available);
        setFilteredTenants(available);
      } catch (err) {
        console.error('Error fetching tenants:', err);
      }
    };
    if (isOpen) fetchTenants();
  }, [isOpen]);

  useEffect(() => {
    setFilteredTenants(
      tenants.filter(t =>
        t.TenantName.toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  }, [searchQuery, tenants]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  const handleTenantClick = tenant => {
    setSelectedTenant(tenant);
    setShowConfirmModal(true);
  };

  const handleConfirmImpersonation = async () => {
    try {
      await apiClient.post('/impersonate', {
        TargetTenantName: selectedTenant.TenantName,
      });
      
      BrandingService.clearAll();
      dispatch(resetTheme());
      await refreshProfile();
      setShowConfirmModal(false);
      onClose();
    } catch (error) {
      console.error('Impersonation failed:', error);
      setErrorMessage('Failed to impersonate tenant. Please try again.');
      setShowErrorModal(true);
    }
  };
  
  if (!isOpen) return null;

  return (
    <div className="switch-tenant-modal" ref={modalRef}>
      <div className="switch-tenant-header">
        <input
          type="text"
          placeholder="Search tenants..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="tenant-search"
        />
      </div>
      <div className="switch-tenant-body">
        {filteredTenants.length > 0 ? (
          filteredTenants.map((tenant, idx) => (
            <div
              key={idx}
              className="tenant-item"
              onClick={() => handleTenantClick(tenant)}
            >
              {tenant.TenantName}
            </div>
          ))
        ) : (
          <div className="no-results">No tenants found</div>
        )}
      </div>

      {showConfirmModal && (
        <ConfirmationModal
          isOpen={showConfirmModal}
          message={`Are you sure you want to switch to "${selectedTenant?.TenantName}"?`}
          confirmLabel="Yes"
          cancelLabel="No"
          onConfirm={() => {
            handleConfirmImpersonation();
            setShowConfirmModal(false);
            onClose();
          }}
          onCancel={() => {
            setShowConfirmModal(false);
            setSelectedTenant(null);
          }}
        />
      )}
      {showErrorModal && (
        <ConfirmationModal
          isOpen={showErrorModal}
          message={errorMessage}
          confirmLabel="OK"
          showCancel={false}
          isDanger={false}
          onConfirm={() => setShowErrorModal(false)}
        />
      )}
    </div>
  );
};

export default SwitchTenantModal;
